import {
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  InteractionStatus,
  PromptValue,
  type IPublicClientApplication,
} from '@azure/msal-browser'
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useIntl } from 'react-intl'

import { SignInAgainError } from '@/shared/api/api-error'
import { onUnauthorized } from '@/shared/api/unauthorized'
import type { AuthConfig } from '@/shared/config/runtime-config'

import { AuthStateProvider, type AuthState, type CurrentUser } from './auth-state'
import { isFreshSignInRequired, requireFreshSignIn } from './fresh-sign-in'
import { HopAuthProvider } from './hop-auth-provider'
import { signOutForgettingFailure } from './kept-sign-in-failure'
import { isPublicPageShown } from './public-page'
import { redirectsAllowed, signOutRedirectAllowed } from './redirect-hold'
import { clearReturnPath, rememberReturnPath } from './return-path'
import { SessionProvider } from './session'
import type { SessionClient } from './session-client'
import { buildSignInRequest } from './sign-in-hints'
import {
  clearHopToken,
  displayNameOf,
  isHopTokenChange,
  isHopTokenUsable,
  isSignedInThroughHop,
  loadHopToken,
  readHopToken,
  SignInRenewalRequiredError,
} from './sign-in-hop'
import { clearSignedInStorage } from './signed-in-storage'

export { useAuth, type AuthState, type CurrentUser } from './auth-state'

/** Raised when sign-in is asked for in an environment that has it switched off. */
export class AuthDisabledError extends Error {
  constructor() {
    super('Authentication is disabled in this environment, so there is nobody to sign in.')
    this.name = 'AuthDisabledError'
  }
}

/**
 * Supplies the authentication state and the site's session that follows it. The implementation is
 * chosen once here, so nothing downstream decides access on {@link AuthState.mode}; only
 * presentation reads it. `sessionClient` replaces the session's API calls, for tests.
 */
export function AuthProvider(props: {
  readonly config: AuthConfig
  readonly instance: IPublicClientApplication | null
  readonly sessionClient?: SessionClient | undefined
  readonly children: ReactNode
}) {
  const children = <SessionProvider client={props.sessionClient}>{props.children}</SessionProvider>

  if (props.config.mode === 'disabled') {
    return <DisabledAuthProvider>{children}</DisabledAuthProvider>
  }

  // B2C returns only to the sign-in site, so this address signs in through it (SM-04).
  if (isSignedInThroughHop(props.config)) {
    return <HopAuthProvider>{children}</HopAuthProvider>
  }

  // Throw, never fall back to the disabled provider: that would send every request unauthenticated.
  if (props.instance === null) {
    throw new Error(
      'Authentication mode is "b2c" but no MSAL client was supplied. This is a defect in '
        + 'bootstrap(), not a configuration problem.',
    )
  }

  return (
    <MsalProvider instance={props.instance}>
      <MsalAuthProvider
        scopes={props.config.apiScopes}
        authority={props.config.authority}
        redirectPath={props.config.redirectPath}
      >
        {children}
      </MsalAuthProvider>
    </MsalProvider>
  )
}

/** The MSAL-backed implementation. */
function MsalAuthProvider(props: {
  readonly scopes: readonly string[]
  readonly authority: string
  readonly redirectPath: string
  readonly children: ReactNode
}) {
  const { instance, accounts, inProgress } = useMsal()
  const isMsalAuthenticated = useIsAuthenticated()
  const { scopes, authority, redirectPath } = props

  // A one-time sign-in link for this site (SM-39) signs in with its own policy's token, which MSAL
  // does not keep: held as a hop site holds B2C's, until it expires or anyone signs in or out here.
  const [linkToken, setLinkToken] = useState(readHopToken)
  useEffect(() => {
    const reload = () => {
      void loadHopToken().then(setLinkToken)
    }
    const onStorage = (event: StorageEvent) => {
      if (isHopTokenChange(event)) {
        reload()
      }
    }
    window.addEventListener('storage', onStorage)
    const stopListening = onUnauthorized(reload)
    return () => {
      window.removeEventListener('storage', onStorage)
      stopListening()
    }
  }, [])
  const isAuthenticated = linkToken !== null || isMsalAuthenticated

  // MsalProvider reports Startup on first render whatever bootstrap() awaited. Logout clears the
  // cache before navigating; acting on that would make the guard remember the page being left.
  const isResolving =
    inProgress === InteractionStatus.Startup
    || inProgress === InteractionStatus.HandleRedirect
    || inProgress === InteractionStatus.Logout

  // Another tab signing out empties the cache every tab shares, but MSAL tells MsalProvider here
  // nothing it acts on: it relays no logout for a sign-out that names no account, and its
  // account-changed message arrives before the accounts are gone. So this tab notices on its own,
  // from the storage event that emptying raises, or from a 401 if that event was missed.
  const hasAccounts = accounts.length > 0
  useEffect(() => {
    if (!hasAccounts) {
      return undefined
    }

    const noticeSignOut = () => {
      if (instance.getAllAccounts().length === 0) {
        clearSignedInStorage()
        // Raises the event MsalProvider reads the accounts again on.
        instance.setActiveAccount(null)
      }
    }
    const onStorage = (event: StorageEvent) => {
      if (event.storageArea === window.localStorage && (event.key === null || event.key.startsWith('msal.'))) {
        noticeSignOut()
      }
    }

    window.addEventListener('storage', onStorage)
    const stopListening = onUnauthorized(noticeSignOut)
    return () => {
      window.removeEventListener('storage', onStorage)
      stopListening()
    }
  }, [instance, hasAccounts])

  // Read the account per call, so a sign-in completed after this memo was built is still seen.
  const state = useMemo<AuthState>(() => {
    const requestScopes = [...scopes]

    function activeAccount() {
      return instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null
    }

    // The link's sign-in ends with its token, or with any sign-in or sign-out here.
    async function dropLinkToken(): Promise<void> {
      if (readHopToken() !== null) {
        await clearHopToken()
        setLinkToken(null)
      }
    }

    return {
      mode: 'b2c',
      isResolving,
      isAuthenticated,
      user: linkToken !== null ? { displayName: displayNameOf(linkToken.accessToken) } : resolveUser(instance, accounts),

      async signIn(email, site): Promise<void> {
        await redirectsAllowed()
        await ignoreAbandonedRedirect(
          instance.loginRedirect({
            scopes: requestScopes,
            ...buildSignInRequest(email, site ?? null, authority),
            ...(isFreshSignInRequired() && { prompt: PromptValue.LOGIN }),
          }),
        )
      },

      async signOut(): Promise<void> {
        // Otherwise it would decide where the next sign-in on this tab lands.
        clearReturnPath()
        clearSignedInStorage()
        await signOutForgettingFailure(redirectPath, async () => {
          await dropLinkToken()
          await signOutRedirectAllowed()
          await ignoreAbandonedRedirect(instance.logoutRedirect())
        })
      },

      async forgetSignIn(): Promise<void> {
        clearReturnPath()
        clearSignedInStorage()
        requireFreshSignIn()
        await dropLinkToken()
        await instance.clearCache()
        // Clearing emits no event when no account was active; this tells MsalProvider the accounts changed.
        instance.setActiveAccount(null)
      },

      async getAccessToken(options): Promise<string | null> {
        // The link's token while it lasts, a forced refresh included, as after a 401 that only says no
        // session has started yet. Nothing renews it without B2C, so once it ends the person signs in
        // again; a check the app makes on its own never signs them out, as on a hop site.
        const held = readHopToken()
        if (held !== null) {
          if (isHopTokenUsable(held)) {
            return held.accessToken
          }
          if (options?.isSilent === true) {
            throw new SignInRenewalRequiredError()
          }
          await dropLinkToken()
        }

        const account = activeAccount()
        if (account === null) {
          return null
        }

        try {
          const result = await instance.acquireTokenSilent({
            scopes: requestScopes,
            account,
            forceRefresh: options?.isForcedRefresh ?? false,
          })
          return result.accessToken
        } catch (error: unknown) {
          if (error instanceof InteractionRequiredAuthError && options?.isSilent !== true) {
            // Decided once nothing holds redirects, as the current site's lookup does, so the page
            // shown is known by then. A public page never sends anyone to B2C on its own: it reads
            // on signed out, and a write fails, asking the person to sign in again.
            await redirectsAllowed()
            if (isPublicPageShown()) {
              if (options?.isWrite === true) {
                throw new SignInAgainError('The sign-in can no longer renew without B2C, so the write was not sent.')
              }
              return null
            }
            // Redirect to B2C. Remember the page first: the route guard only does that for
            // signed-out callers, so the callback would otherwise land on the home page. The
            // callback itself keeps the page the guard remembered.
            const { pathname, search, hash } = window.location
            if (pathname !== redirectPath) {
              rememberReturnPath(`${pathname}${search}${hash}`)
            }
            await instance.acquireTokenRedirect({ scopes: requestScopes, account })
            return null
          }
          throw error
        }
      },
    }
  }, [instance, accounts, isAuthenticated, isResolving, scopes, authority, redirectPath, linkToken])

  return <AuthStateProvider value={state}>{props.children}</AuthStateProvider>
}

/**
 * Authentication switched off: reports a signed-in local developer, matching the API's mode.
 * `AuthDisabledBanner` on every page keeps it from passing as a real sign-in.
 */
function DisabledAuthProvider(props: { readonly children: ReactNode }) {
  const intl = useIntl()
  const displayName = intl.formatMessage({ id: 'shell.auth.localDeveloper' })

  const state = useMemo<AuthState>(
    () => ({
      mode: 'disabled',
      isResolving: false,
      isAuthenticated: true,
      user: { displayName },

      signIn(): Promise<void> {
        // Rejects rather than no-ops, so the login page says sign-in is unavailable.
        return Promise.reject(new AuthDisabledError())
      },

      signOut(): Promise<void> {
        return Promise.reject(new AuthDisabledError())
      },

      // Nothing is cached to forget.
      forgetSignIn(): Promise<void> {
        return Promise.resolve()
      },

      // The API in the matching mode validates no token.
      getAccessToken(): Promise<string | null> {
        return Promise.resolve(null)
      },
    }),
    [displayName],
  )

  return <AuthStateProvider value={state}>{props.children}</AuthStateProvider>
}

/**
 * Awaits a redirect, ignoring MSAL's `timed_out`: that is the user pressing Back on the B2C page
 * (a back/forward cache restore), not a failure.
 */
async function ignoreAbandonedRedirect(redirect: Promise<void>): Promise<void> {
  try {
    await redirect
  } catch (error: unknown) {
    if (error instanceof BrowserAuthError && error.errorCode === BrowserAuthErrorCodes.timedOut) {
      return
    }
    throw error
  }
}

/** Picks a display name off the signed-in account, or `null` when nobody is signed in. */
function resolveUser(
  instance: IPublicClientApplication,
  accounts: readonly { readonly name?: string; readonly username: string }[],
): CurrentUser | null {
  const account = instance.getActiveAccount() ?? accounts[0] ?? null
  if (account === null) {
    return null
  }

  // `name` may not be issued. Display only: identity is (iss, sub), on the API side.
  return { displayName: account.name ?? account.username }
}
